// Тарифы: четыре штуки, у каждого имя и цена. SMY-61 · P5
//
// Лимитов здесь НЕТ, и это главное в файле. С SMY-57 они живут в
// конфигурации (`units.limits` в config.ts), потому что после четырёх недель
// беты их придётся пересчитать по фактической себестоимости (SMY-89) — а
// пересчёт, требующий релиза, не случится в тот день, когда он нужен.
//
// Здесь остались имя и цена: их меняет не админка, а оферта.
//
// 06.09.2026 сетка пересобрана: появился входной «Старт» за 149 ₽, «Плюс»
// и «Про» переразложены по единицам, «Профессиональный» переименован в «Про».
// Тариф добавляется не одним числом: `tariff` — это перечисление в БД, и на
// каждое новое значение нужна миграция (0020).
//
// 09.09.2026 сетка пересобрана снова, из админки: лимиты удвоены на каждой
// ступени (см. шапку `units.limits` в config.ts). Цены не тронуты.
//
// ⚠️ РИСК «СТАРТА» БЫЛ ЗАПИСАННЫМ, А СТАЛ ДРУГИМ ПО ПОРЯДКУ ВЕЛИЧИНЫ. При
// полной выработке он стоит нам 177,35 ₽ против 137,83 ₽ нетто — 129%, то
// есть тариф убыточен сам по себе: −39,52 ₽ с подписчика в месяц, а по
// потолку (весь лимит вопросов поиском, документы сканами, правка
// изображения у каждого) −177,37 ₽. Безубыточным он становится с цены
// 191,73 ₽ по ожидаемой смеси.
//
// И главное отличие от прежней записи: «Старт» выпадает из гейта «COGS ≤ 30%»
// уже на СРЕДНЕЙ выработке (32%), а не только на полной. Раньше средняя
// проходила с запасом в 14,2%, и именно это делало риск приемлемым.
//
// Виноваты изображения: 40 штук стоят 113,57 ₽ — 64% себестоимости тарифа и
// 82% всей выручки с него.
//
// Прежнее решение — «продаётся ради входа в платную базу, а не ради маржи, и
// правится лимитом из админки, когда бета покажет выработку (SMY-89)» —
// принималось при убытке 9,22 ₽ по потолку и прибыли на средней. К нынешним
// числам оно не применялось: их последствия разбираются отдельно
// (SMY-185). Разбор — economics/cogs.md.

export const TARIFF_IDS = ['free', 'start', 'plus', 'pro'] as const
export type TariffId = (typeof TARIFF_IDS)[number]

/**
 * Место в очереди. Меньше — раньше.
 *
 * ОЧЕРЕДИ ПОКА НЕТ, и это записанное состояние, а не забытая половина.
 * Движок запускает workflow сразу и не ограничивает одновременность (см.
 * шапку apps/api/src/ai/workflow.ts: «чего здесь намеренно нет — очереди»).
 * Пока ждать не приходится, приоритет ничего не решает, и порядок здесь —
 * заготовка под тот день, когда нагрузка заставит ограничивать одновременные
 * запуски.
 *
 * Делать очередь сейчас значило бы завести ожидание там, где его нет, — и без
 * поддержки в дизайне: экран 05 показывает выполнение, а состояния «ты в
 * очереди» на нём не нарисовано.
 */
export const QUEUE_PRIORITY: Record<TariffId, number> = { free: 3, start: 2, plus: 1, pro: 0 }

/** Как скорость называется на экране 10. */
export const TARIFF_SPEED: Record<TariffId, string> = {
  free: 'Обычная очередь',
  // У «Старта» та же подпись, что у бесплатного, и это честнее выдуманной
  // третьей ступени: очереди нет вовсе, а обещать скорость, которой не
  // отличаемся, — то же самое, что рисовать её на экране без движка.
  start: 'Обычная очередь',
  plus: 'Приоритет',
  pro: 'Максимальный приоритет',
}

export interface Tariff {
  id: TariffId
  label: string
  /** Рубли в месяц. */
  price: number
}

export const TARIFFS: Record<TariffId, Tariff> = {
  free: { id: 'free', label: 'Бесплатный', price: 0 },
  start: { id: 'start', label: 'Старт', price: 149 },
  plus: { id: 'plus', label: 'Плюс', price: 499 },
  pro: { id: 'pro', label: 'Про', price: 1999 },
}

// --- Откуда у пользователя тариф. SMY-61 · P5 Э5.2 --------------------------

/**
 * Два независимых источника, и оба работают одновременно.
 *
 * Так устроен MVP: подписку продаёт веб (SMY-62), а админка выдаёт любой тариф
 * бесплатно на время беты (SMY-95). Хранить в модели только сам тариф значило
 * бы не суметь ни корректно обработать истечение, ни отделить платящих от
 * бесплатных в аналитике — то есть завысить выручку ровно на тех, кому её
 * подарили.
 */
export const ENTITLEMENT_SOURCES = ['subscription', 'grant'] as const
export type EntitlementSource = (typeof ENTITLEMENT_SOURCES)[number]

/** Право на тариф: что, откуда и на какой срок. */
export interface Entitlement {
  tariff: TariffId
  source: EntitlementSource
  startsAt: Date
  /** Пусто — бессрочно. Так выдаёт админка на время беты. */
  endsAt?: Date | undefined
  revokedAt?: Date | undefined
}

/**
 * Старшинство тарифов. Нужно не для показа, а для выбора между правами:
 * когда их два, действует лучшее.
 */
export const TARIFF_RANK: Record<TariffId, number> = { free: 0, start: 1, plus: 2, pro: 3 }

/** Действующий тариф и с какого момента. */
export interface ActiveTariff {
  tariff: TariffId
  /** Пусто у бесплатного: он ниоткуда не взялся, он состояние по умолчанию. */
  source?: EntitlementSource
  /**
   * Когда начался ТЕКУЩИЙ тариф. От него считается расчётный период: экран 10
   * обещает «Продлится 3 октября», а не «первого числа».
   */
  since: Date
  /** Когда закончится. Пусто — бессрочно либо бесплатный. */
  until?: Date
}

/**
 * Какой тариф действует и с какого момента.
 *
 * ДЕЙСТВУЕТ ЛУЧШЕЕ ИЗ ПРАВ, а не последнее выданное. Это прямой ответ на
 * вопрос «что происходит при выдаче тарифа пользователю с активной подпиской»:
 * человек не должен терять оплаченное оттого, что ему что-то подарили. Когда
 * подарок кончится, подписка снова окажется лучшим правом и продолжит
 * действовать сама.
 *
 * `since` у бесплатного — не момент появления аккаунта, а момент, когда
 * бесплатный НАСТУПИЛ: конец последнего права. Иначе после окончания подписки
 * расчётный период отсчитывался бы от начала и втянул бы в себя траты
 * оплаченного месяца — то есть человек вышел бы на бесплатный тариф уже с
 * минусом.
 */
export function effectiveTariff(
  entitlements: readonly Entitlement[],
  at: Date,
  /**
   * Когда аккаунт появился (`users.created_at`): с этого момента начинается
   * бесплатный тариф у тех, кто ничего не имел.
   *
   * Назывался `registeredAt` до 09.09.2026 и врал бы с этого дня: аккаунт
   * заводит ПЕРВЫЙ ЗАПУСК приложения, а регистрация случается позже и может
   * не случиться вовсе (SMY-150). Колонка та же, смысл другой — а имя
   * аргумента продолжало бы утверждать прежнее.
   */
  startedAt: Date,
): ActiveTariff {
  const active = entitlements.filter(
    (e) =>
      e.revokedAt == null &&
      e.startsAt.getTime() <= at.getTime() &&
      (e.endsAt == null || e.endsAt.getTime() > at.getTime()),
  )

  const best = [...active].sort((a, b) => {
    const rank = TARIFF_RANK[b.tariff] - TARIFF_RANK[a.tariff]
    if (rank !== 0) return rank
    // При равном тарифе выигрывает то, что кончится позже: бессрочное сильнее
    // срочного, а из двух срочных — дальнее.
    if (a.endsAt == null) return -1
    if (b.endsAt == null) return 1
    return b.endsAt.getTime() - a.endsAt.getTime()
  })[0]

  if (best != null) {
    return {
      tariff: best.tariff,
      source: best.source,
      since: best.startsAt,
      ...(best.endsAt != null ? { until: best.endsAt } : {}),
    }
  }

  const ended = entitlements
    .map((e) => e.revokedAt ?? e.endsAt)
    .filter((d): d is Date => d != null && d.getTime() <= at.getTime())
    .sort((a, b) => b.getTime() - a.getTime())[0]

  return { tariff: 'free', since: ended ?? startedAt }
}
